import React, { useEffect, useState } from "react";
import styles from "./ConfirmProcessStatusIndex.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
    CircularProgress,
    createStyles,
    IconButton,
    LinearProgress,
    makeStyles,
    withStyles,
} from "@material-ui/core";
import PuiTypography from "../../../components/elements/PuiTypography";
import { LoadProcessStatus, LoadProcessStatusRepeat } from "../../../store/actions/ExVideoActions";
import { AppState } from "../../../root/RootReducer";
import { getStatusProgress, getVideoStatus } from "../../../utils/LogicUtils";
import { ExtractExtention, isArrayEuqual, isNullorUndefined } from "../../../utils/ObjectUtils";
import PuiTooltip from "../../../components/elements/PuiTooltip ";
import ClearIcon from "@material-ui/icons/Clear";
import VideoIcon from "@material-ui/icons/Theaters";
import { DeleteVideo } from "../../../common/video/VideoActions";
import PuiDialog from "../../../components/widgets/PuiDialog";

const NormalLinearProgress = withStyles(() =>
    createStyles({
        root: {
            height: 12,
            borderRadius: 15,
        },
        colorPrimary: {
            backgroundColor: "var(--gray-85)",
        },
        bar: {
            borderRadius: 15,
            backgroundColor: "forestgreen",
        },
    })
)(LinearProgress);

const ErrorLinearProgress = withStyles(() =>
    createStyles({
        root: {
            height: 12,
            borderRadius: 15,
        },
        colorPrimary: {
            backgroundColor: "var(--gray-85)",
        },
        bar: {
            borderRadius: 15,
            backgroundColor: "var(--red)",
        },
    })
)(LinearProgress);

const useStyles = makeStyles(() =>
    createStyles({
        delIcon: {
            height: "35px",
            width: "35px",
            marginRight: "-15px",
        },
    })
);

export interface StatusObject {
    id: string;
    title: string;
    process: number;
    status: string;
    updated: Date;
}

export default function ConfirmProcessStatusIndex() {
    const dispatch = useDispatch();
    const classes = useStyles();

    // パラメータ初期化
    const [videoData, setVideoData] = useState<StatusObject[]>([]);
    const [targetId, setTargetId] = useState<string>("");
    const [targetTitle, setTargetTitle] = useState<string>("");

    // 処理状態パラメータ
    const { videos, isLoading } = useSelector((state: AppState) => ({
        videos: state.exVideo.processStatus.videos,
        isLoading: state.exVideo.processStatus.isLoading,
    }));
    const { userInfo } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
    }));

    // 制御パラメータ
    const [openDialog, setOpenDialog] = React.useState(false);

    // ビデオ処理情報取得
    useEffect(() => {
        dispatch(LoadProcessStatus(userInfo ? parseInt(userInfo.id) : 1));

        const timer = setInterval(() => {
            dispatch(LoadProcessStatusRepeat(userInfo ? parseInt(userInfo.id) : 1));
        }, 5000);
        return () => {
            clearInterval(timer);
        };
    }, [dispatch, userInfo]);

    // ビデオ処理情報設定
    useEffect(() => {
        const dataList: StatusObject[] = [];
        if (Array.isArray(videos)) {
            videos.forEach((element) => {
                dataList.push({
                    id: element.id,
                    title: element.title,
                    process: getStatusProgress(element.status),
                    status: getVideoStatus(element.status),
                    updated: element.updated,
                });
            });
        }
        if (!isArrayEuqual(videoData, dataList)) {
            setVideoData(dataList);
        }
    }, [videos, videoData]);

    // ビデオ削除ボタンイベント
    const handleDelVideo = (id: string, title: string) => {
        setTargetId(id);
        setTargetTitle(title);
        setOpenDialog(true);
    };

    // ダイヤログを閉じる
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // ダイヤログキャンセルボタンイベント
    const handleDialogCancel = () => {
        handleCloseDialog();
    };

    // ダイヤログ確認ボタンイベント
    const handleDialogConfirm = () => {
        if (!isNullorUndefined(targetId)) {
            const targetIndex = videos.findIndex((video) => video.id === targetId);
            const type = ExtractExtention(videos[targetIndex].fileName).replace(/^./, "");
            dispatch(DeleteVideo(parseInt(targetId), type));
            dispatch(LoadProcessStatus(userInfo ? parseInt(userInfo.id) : 1));
        }

        setTargetId("");
        setTargetTitle("");
        handleCloseDialog();
    };

    // 表示待機
    if (isLoading) {
        return (
            <div className={styles.noContent}>
                <CircularProgress size={60} style={{ color: "var(--loading-bg)" }} />
            </div>
        );
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.title}>
                    <PuiTypography size={24} bold={true} color={"black"}>
                        処理中のVideo一覧
                    </PuiTypography>
                </div>
                {videoData.length > 0 && (
                    <>
                        <div className={styles.listContainer}>
                            {videoData.map((video, index) => (
                                <div key={video.id}>
                                    <div className={styles.listItem}>
                                        <div
                                            className={styles.itemTitle}
                                            data-tip={video.title}
                                            data-for="itemTitle"
                                        >
                                            <VideoIcon
                                                color="secondary"
                                                style={{
                                                    color: "var(--main)",
                                                    fontSize: "18px",
                                                    marginRight: "2px",
                                                }}
                                            />
                                            <PuiTypography bold={true} color={"black"}>
                                                {video.title}
                                            </PuiTypography>
                                        </div>
                                        <PuiTooltip id="itemTitle" />
                                        {video.status.includes("失敗") && (
                                            <ErrorLinearProgress
                                                variant="determinate"
                                                value={video.process}
                                            />
                                        )}
                                        {!video.status.includes("失敗") && (
                                            <NormalLinearProgress
                                                variant="determinate"
                                                value={video.process}
                                                valueBuffer={video.process + 10}
                                            />
                                        )}
                                        <div>
                                            <PuiTypography color="gray-61">
                                                {video.status}
                                            </PuiTypography>
                                            {video.status.includes("失敗") &&
                                                !isNullorUndefined(video.updated) && (
                                                    <PuiTypography color="gray-61">{`（${
                                                        String(video.updated)
                                                            .replace("T", " ")
                                                            .replaceAll("-", "/")
                                                            .split(".")[0]
                                                    }）`}</PuiTypography>
                                                )}
                                        </div>
                                        {video.status.includes("失敗") && (
                                            <IconButton
                                                color="secondary"
                                                component="span"
                                                className={classes.delIcon}
                                                onClick={() =>
                                                    handleDelVideo(video.id, video.title)
                                                }
                                            >
                                                <ClearIcon
                                                    style={{
                                                        color: "var(--red)",
                                                        fontSize: "30px",
                                                    }}
                                                />
                                            </IconButton>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <PuiDialog
                    openDialog={openDialog}
                    title={"下記ビデオを削除してもよろしいでしょうか？"}
                    message={`・${targetTitle}`}
                    yesLabel={"削除"}
                    noLabel={"キャンセル"}
                    handleCloseDialog={handleCloseDialog}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                />
            </div>
        </>
    );
}
